import React, { useState } from "react";
import { Link, Redirect } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { login, authErrorMessage } from "../_redux/authCrud";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { shallowEqual, useSelector } from "react-redux";
import PasswordInput from "../../../../components/PasswordInput";
import { peekPostLoginPath } from "../../../../components/postLoginRedirect";

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
    email: "",
    password: ""
};

function Login(props) {
    const { intl } = props;
    const [loading, setLoading] = useState(false);
    const LoginSchema = Yup.object().shape({
        email: Yup.string()
            .email("Wrong email format")
            .min(3, "Minimum 3 symbols")
            .max(50, "Maximum 50 symbols")
            .required(
                intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                })
            ),
        password: Yup.string()
            .min(3, "Minimum 3 symbols")
            .max(50, "Maximum 50 symbols")
            .required(
                intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                })
            )
    });

    const enableLoading = () => {
        setLoading(true);
    };

    const disableLoading = () => {
        setLoading(false);
    };

    const getInputClasses = fieldname => {
        if (formik.touched[fieldname] && formik.errors[fieldname]) {
            return "is-invalid";
        }

        if (formik.touched[fieldname] && !formik.errors[fieldname]) {
            return "is-valid";
        }

        return "";
    };

    const formik = useFormik({
        initialValues,
        validationSchema: LoginSchema,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            enableLoading();
            setTimeout(() => {
                login(values.email, values.password)
                    .then(({ data: { accessToken } }) => {
                        disableLoading();

                        props.login(accessToken);
                    })
                    .catch(err => {
                        const data = err.response && err.response.data;
                        if (data && data.code === "EMAIL_NOT_VERIFIED") {
                            setStatus(
                                <>
                                    Please verify your email address before logging in.{" "}
                                    <Link to="/auth/resend-verification">Resend verification email</Link>
                                </>
                            );
                        } else if (err.response && err.response.status === 429) {
                            setStatus(authErrorMessage(err));
                        } else {
                            setStatus(
                                intl.formatMessage({
                                    id: "AUTH.VALIDATION.INVALID_LOGIN"
                                })
                            );
                        }
                    })
                    .finally(() => {
                        disableLoading();
                        setSubmitting(false);
                    });
            }, 1000);
        }
    });

    const { isAuthorized } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null
        }),
        shallowEqual
    );

    if (isAuthorized) {
        // QR-based Asset Identification - if the visitor arrived here via
        // an unauthenticated /scan/:qrToken (see ScanRedirectCapture.js),
        // send them back to finish that flow instead of the dashboard.
        const pendingScanPath = peekPostLoginPath();
        if (pendingScanPath) {
            return <Redirect to={pendingScanPath} />;
        }
        // If user is authorized, redirect to the dashboard
        return <Redirect to="/dashboard" />;
    }
    return (
        <>
            <div className="position-absolute top-0 right-0 text-right mt-5 mb-15 mb-lg-0 flex-column-auto justify-content-center py-5 px-10">
                <span className="font-weight-bold text-dark-50">
                    Don't have an account yet?
                </span>
                <Link
                    to="/auth/registration"
                    className="font-weight-bold ml-2"
                    id="kt_login_signup"
                >
                    Sign Up!
                </Link>
            </div>
            <div className="login-form login-signin" id="kt_login_signin_form">
                {/* begin::Head */}
                <div className="text-center mb-10 mb-lg-20">
                    <br />
                    <Link to="/">
                        <img
                            src={`${toAbsoluteUrl(
                                "/media/client-logos/logo.png"
                            )}`}
                            style={{ height: "25%", width: "25%" }}
                        />
                    </Link>
                    <h3 className="font-size-h1">
                        <FormattedMessage id="AUTH.LOGIN.TITLE" />
                    </h3>
                    <p className="text-muted font-weight-bold">
                        Enter your details to login to your account
                    </p>
                </div>
                {/* end::Head */}

                {/*begin::Form*/}
                <form
                    onSubmit={formik.handleSubmit}
                    className="form fv-plugins-bootstrap fv-plugins-framework"
                >
                    {formik.status && (
                        <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                            <div className="alert-text font-weight-bold">
                                {formik.status}
                            </div>
                        </div>
                    )}

                    <div className="form-group fv-plugins-icon-container">
                        <input
                            placeholder="Email"
                            type="email"
                            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
                                "email"
                            )}`}
                            name="email"
                            {...formik.getFieldProps("email")}
                        />
                        {formik.touched.email && formik.errors.email ? (
                            <div className="fv-plugins-message-container">
                                <div className="fv-help-block">
                                    {formik.errors.email}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="form-group fv-plugins-icon-container">
                        <PasswordInput
                            placeholder="Password"
                            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
                                "password"
                            )}`}
                            name="password"
                            {...formik.getFieldProps("password")}
                        />
                        {formik.touched.password && formik.errors.password ? (
                            <div className="fv-plugins-message-container">
                                <div className="fv-help-block">
                                    {formik.errors.password}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
                        <Link
                            to="/auth/forgot-password"
                            className="text-dark-50 text-hover-primary my-3 mr-2"
                            id="kt_login_forgot"
                        >
                            <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
                        </Link>
                        <button
                            id="kt_login_signin_submit"
                            type="submit"
                            disabled={formik.isSubmitting}
                            className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
                        >
                            <span>Sign In</span>
                            {loading && (
                                <span className="ml-3 spinner spinner-white"></span>
                            )}
                        </button>
                    </div>


                </form>
                {/*end::Form*/}
            </div>
        </>
    );
}

export default injectIntl(connect(null, auth.actions)(Login));
